import { useTranslation } from "react-i18next";
import { Form, Icon, Input } from "semantic-ui-react";

import { Element } from "components/ReactScroll/index";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { copyToClipboard, isAdmin } from "utils/common";

import type { DealerLocation } from "models";
import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

type AiTabletProps = {
  tabId: string;
  location: DealerLocation;
};

export const AiTablet = ({ tabId, location }: AiTabletProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { watch, setValue } = FormHelper.useFormContext();
  const [isTyreScannerEnabled, isAaEquipmentEnabled, tyreScanners] = watch(["is_tire_scanner_enabled", "is_aaequipment_enabled", "aa_equipment_tyre_scanners"]);

  if (!user || !isAdmin(user.role_id)) return null;

  return (
    <Element name={tabId} className="dealer-location-section">
      <div className="section-title">
        <div>
          <Icon className="robot" />
        </div>
        <span className="section-title-text">{t("v8_ai_and_tablet", "AI & Tablet")}</span>
      </div>

      <div className="dealer-location-columns">
        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_tablet", "Tablet")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox
              name="is_assigned_wo_status_change_notification_enabled"
              label={t("v8_notify_change_of_status_on_assigned_wo", "Notify change of status on assigned WO")}
            />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_tablet_translation_ai_enabled" label={t("v8_translation_ai_on_tablet", "Translation AI on tablet")} />
          </Form.Field>
        </div>

        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_ai_summary", "AI Summary")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="generate_ai_summary" label={t("v8_generate_ai_summary", "Generate AI Summary")} />
          </Form.Field>

          <div className="dealer-location-subheading">{t("v8_tire_scanner", "Tire Scanner")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_tire_scanner_enabled" label={t("v8_tire_scanner", "Tire Scanner")} />
          </Form.Field>

          {isTyreScannerEnabled && (
            <>
              <Form.Field>
                <label>{t("v8_tire_scanner_private_key", "Tire Scanner private key")}</label>
                <div className="dealer-location-readonly-value">
                  <Icon className="copy" title={t("v8_copy_to_clipboard", "Copy to clipboard")} onClick={() => copyToClipboard(location.tire_scanner_private_key)} />
                  <div>{location.tire_scanner_private_key}</div>
                </div>
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox name="is_aaequipment_enabled" label={t("v8_is_aaequipment_enabled", "Is AAEquipment Enabled")} />
              </Form.Field>
              {tyreScanners?.map((scanner, idx) => (
                <Form.Group key={scanner.short_code}>
                  <Form.Field width={10}>
                    <label>{t("v8_serial_number", "Serial Number")}</label>
                    <Input
                      disabled={!isAaEquipmentEnabled}
                      value={scanner.serial_number}
                      placeholder={t("v8_enter_serial_number", "Enter Serial Number")}
                      onChange={(_, { value }) => setValue("aa_equipment_tyre_scanners", tyreScanners.with(idx, { ...scanner, serial_number: value }))}
                    />
                  </Form.Field>
                  <Form.Field width={6}>
                    <label>{t("v8_pairing_number", "Pairing Number")}</label>
                    <Input disabled={!isAaEquipmentEnabled} readOnly value={scanner.short_code} />
                  </Form.Field>
                </Form.Group>
              ))}
            </>
          )}
        </div>
      </div>
    </Element>
  );
};
